import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import { Row, Col, Alert } from 'react-bootstrap';
import get from 'lodash/get';
import SMFormFieldToggle from '../../../../components/stateless/SMFormFieldToggle';
import withForm from '../../../../../../admin/hocs/withForm';
import Translation from '../../../../../components/stateless/Translation';
import CheckoutSubmitButton from '../CheckoutSubmitButton';
import CheckoutFormFieldText from '../CheckoutFormFieldText';
import CheckoutFormFieldPhone from '../CheckoutFormFieldPhone';
import CheckoutFormStateDropdown from '../CheckoutFormStateDropdown';
import checkoutStyles from '../styles.scss';
import {
  TRACK_CLICK_CHECKOUT_RESERVATION_STEP_ONE_SUBMIT,
  TRACK_CLICK_CHECKOUT_STEP_ONE_SUBMIT,
  TRACK_CHECKOUT_STEP,
} from '../../../../../../src/web/utils/dataLayer';
import { GB } from '../../../../../../src/shared/i18n';

const { MAPBOX_API_TOKEN } = process.env;

const propTypes = {
  formError: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  isReservation: PropTypes.bool.isRequired,
  facility: PropTypes.shape().isRequired,
};

const defaultProps = {};

const validationSchema = object().shape({
  firstName: string()
    .required('widgets.checkout.firstName.required'),
  lastName: string()
    .required('widgets.checkout.lastName.required'),
  phone: object().shape({
    unformattedPhoneNumber: string()
      .matches(/^[0-9|(|)|\-|\s|+]*$/)
      .required('widgets.checkout.phone.required'),
  }),
  email: string()
    .required('widgets.checkout.email.required')
    .email('widgets.checkout.email.invalid'),
  address1: string()
    .when('isReservation', {
      is: false,
      then: string().required('widgets.checkout.address1.required'),
    }),
  city: string()
    .when('isReservation', {
      is: false,
      then: string().required('widgets.checkout.city.required'),
    }),
  state: string()
    .when(['isReservation', 'isFacilityGB'], {
      is: false,
      then: string().required('widgets.checkout.state.required'),
    }),
  postal: string()
    .when('isReservation', {
      is: false,
      then: string().required('widgets.checkout.postal.required'),
    }),
  country: string(),
  activeDutyMilitary: string()
    .required('widgets.checkout.activeDutyMilitary.required'),
  commanderFirstName: string()
    .when('activeDutyMilitary', {
      is: 'yes',
      then: string().required('widgets.checkout.commanderFirstName.required'),
    }),
  commanderLastName: string()
    .when('activeDutyMilitary', {
      is: 'yes',
      then: string().required('widgets.checkout.commanderLastName.required'),
    }),
  unitAddress: string()
    .when('activeDutyMilitary', {
      is: 'yes',
      then: string().required('widgets.checkout.unitAddress.required'),
    }),
  unitCity: string()
    .when('activeDutyMilitary', {
      is: 'yes',
      then: string().required('widgets.checkout.unitCity.required'),
    }),
  unitState: string()
    .when(['activeDutyMilitary', 'isFacilityGB'], {
      is: (activeDutyMilitary, isFacilityGB) => (
        activeDutyMilitary === 'yes'
        && !isFacilityGB
      ),
      then: string().required('widgets.checkout.unitState.required'),
    }),
  unitPostal: string()
    .when('activeDutyMilitary', {
      is: 'yes',
      then: string().required('widgets.checkout.unitPostal.required'),
    }),
});

const initialValues = {
  firstName: '',
  lastName: '',
  phone: {
    country: null,
    phoneNumber: '',
    unformattedPhoneNumber: '',
  },
  email: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postal: '',
  country: '',
  smsOptIn: false,
  isReservation: false,
  isFacilityGB: false,
  activeDutyMilitary: '',
  commanderFirstName: '',
  commanderLastName: '',
  unitAddress: '',
  unitCity: '',
  unitState: '',
  unitPostal: '',
};

const ContactInformationForm = ({
  handleSubmit,
  submitting,
  formError,
  isReservation,
  handleChange,
  facility,
  values,
  setFieldValue,
  setFieldError,
  setFieldTouched,
}) => {
  const [hasTrackedDataLayer, setHasTrackedDataLayer] = useState(false);
  const [AddressAutofillComponent, setAddressAutofillComponent] = useState(null);

  // Load Mapbox AddressAutofill component only on client side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('@mapbox/search-js-react').then(({ AddressAutofill }) => {
        setAddressAutofillComponent(() => AddressAutofill);
      }).catch((error) => {
        console.error('Failed to load Mapbox AddressAutofill:', error);
      });
    }
  }, []);

  useEffect(() => {
    // Set address1 to the value of address1 address-search
    // since Mapbox appends address-search to the name
    if (values['address1 address-search'] !== undefined) {
      setFieldValue('address1', values['address1 address-search']);
    }
  }, [values['address1 address-search'], setFieldValue]);

  useEffect(() => {
    if (!hasTrackedDataLayer && facility) {
      setHasTrackedDataLayer(true);
      TRACK_CHECKOUT_STEP(get(facility, 'unitGroup'), facility, 'new_booking_flow_step1', 'contact info');
    }
  }, [facility, hasTrackedDataLayer]);

  const handleAddress1Blur = (e) => {
    // Manually set formik touched and error for address1
    // due to mapbox address autofill appending address-search to the name
    if (['', undefined].includes(e.target.value)) {
      setFieldTouched('address1', true);
      setFieldError('address1', 'widgets.checkout.address1.required');
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
    >
      {
        formError && (
          <Alert bsStyle="danger">
            <Translation
              id="widgets.checkout.formError"
              defaultMessage="An error was encountered when submitting the form. Please try again."
            />
          </Alert>
        )
      }
      <Row>
        <Col xs={12} md={8}>
          <CheckoutFormFieldText
            required
            name="firstName"
            label={{
              defaultMessage: 'First Name',
              id: 'widgets.checkout.firstName',
            }}
          />
          <CheckoutFormFieldText
            required
            name="lastName"
            label={{
              defaultMessage: 'Last Name',
              id: 'widgets.checkout.lastName',
            }}
          />
          <CheckoutFormFieldText
            required
            name="email"
            label={{
              defaultMessage: 'Email Address',
              id: 'widgets.checkout.email',
            }}
          />
          <CheckoutFormFieldPhone
            required
            name="phone"
            label={{
              defaultMessage: 'Phone Number',
              id: 'widgets.checkout.phone',
            }}
            help={{
              id: 'widgets.checkout.phone.help',
              defaultMessage: 'We will only call about your order',
            }}
          />
          <SMFormFieldToggle
            type="checkbox"
            name="smsOptIn"
            value
            label={(
              <Translation
                id="widgets.checkout.smsOptIn"
                defaultMessage="Text me reminders and notifications about my storage unit"
              />
            )}
          />
        </Col>
      </Row>
      { !isReservation && (
        <>
          <div className={checkoutStyles.divider} />
          <Row>
            <Col xs={12} md={8}>
              <h3>
                <Translation
                  id="widgets.checkout.mailingAddressTitle"
                  defaultMessage="Mailing Address"
                />
              </h3>
            </Col>
          </Row>
          {AddressAutofillComponent && (
            <AddressAutofillComponent
              accessToken={MAPBOX_API_TOKEN}
              options={{
                countries: ['us', 'gb', 'ca'],
              }}
            >
              <Row>
                <Col xs={12} md={8}>
                  <CheckoutFormFieldText
                    required
                    name="address1"
                    label={{
                      defaultMessage: 'Address',
                      id: 'widgets.checkout.address1',
                    }}
                    autoComplete="address-line1"
                    onBlur={handleAddress1Blur}
                  />
                  <CheckoutFormFieldText
                    name="address2"
                    label={{
                      defaultMessage: 'Bldg/Apt/Suite (Optional)',
                      id: 'widgets.checkout.address2',
                    }}
                    autoComplete="address-line2"
                  />
                  <CheckoutFormFieldText
                    required
                    name="city"
                    label={{
                      defaultMessage: 'City',
                      id: 'widgets.checkout.city',
                    }}
                    autoComplete="address-level2"
                  />
                </Col>
              </Row>
              <Row>
                {get(facility, 'address.country') !== GB ? (
                  <Col xs={12} md={4}>
                    <CheckoutFormStateDropdown
                      label={{
                        defaultMessage: 'State',
                        id: 'widgets.checkout.state',
                      }}
                      required
                      name="state"
                      country={get(facility, 'address.country')}
                      onChange={handleChange}
                      emptyOption={{
                        defaultMessage: 'Select State',
                        id: 'widgets.checkout.stateDefault',
                      }}
                      autoComplete="address-level1"
                    />
                  </Col>
                )
                  : null}
                <Col xs={12} md={4}>
                  <CheckoutFormFieldText
                    required
                    name="postal"
                    label={{
                      defaultMessage: 'Zip Code',
                      id: 'widgets.checkout.postal',
                    }}
                    autoComplete="postal-code"
                  />
                </Col>
              </Row>
            </AddressAutofillComponent>
          )}
        </>
      )}
      <div className={checkoutStyles.divider} />
      <Row>
        <Col xs={12} md={8}>
          <h5>
            <Translation
              id="widgets.checkout.activeMilitary"
              defaultMessage="Are you active-duty military?"
            />
            <span className={checkoutStyles.required}>*</span>
          </h5>
        </Col>
      </Row>
      <Row>
        <Col xs={3} md={3}>
          <SMFormFieldToggle
            type="radio"
            name="activeDutyMilitary"
            value="yes"
            label={(
              <span className={checkoutStyles.formFieldToggleLabel}>
                <Translation
                  id="widgets.checkout.yes"
                  defaultMessage="Yes"
                />
              </span>
            )}
          />
        </Col>
        <Col xs={3} md={3}>
          <SMFormFieldToggle
            type="radio"
            name="activeDutyMilitary"
            value="no"
            label={(
              <span className={checkoutStyles.formFieldToggleLabel}>
                <Translation
                  id="widgets.checkout.no"
                  defaultMessage="No"
                />
              </span>
            )}
          />
        </Col>
      </Row>
      {
        values.activeDutyMilitary === 'yes' && (
          <>
            <Row>
              <Col xs={12} md={4}>
                <CheckoutFormFieldText
                  required
                  name="commanderFirstName"
                  label={{
                    defaultMessage: 'Commander\'s First Name',
                    id: 'widgets.checkout.commandersFirstName',
                  }}
                />
              </Col>
              <Col xs={12} md={4}>
                <CheckoutFormFieldText
                  required
                  name="commanderLastName"
                  label={{
                    defaultMessage: 'Commander\'s Last Name',
                    id: 'widgets.checkout.commandersLastName',
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={8}>
                <CheckoutFormFieldText
                  required
                  name="unitAddress"
                  label={{
                    defaultMessage: 'Unit Address',
                    id: 'widgets.checkout.unitAddress1',
                  }}
                />
                <CheckoutFormFieldText
                  required
                  name="unitCity"
                  label={{
                    defaultMessage: 'City',
                    id: 'widgets.checkout.unitCity',
                  }}
                />
              </Col>
            </Row>
            <Row>
              {get(facility, 'address.country') !== GB ? (
                <Col xs={12} md={4}>
                  <CheckoutFormStateDropdown
                    label={{
                      defaultMessage: 'State',
                      id: 'widgets.checkout.unitState',
                    }}
                    required
                    name="unitState"
                    country={get(facility, 'address.country')}
                    onChange={handleChange}
                    emptyOption={{
                      defaultMessage: 'Select State',
                      id: 'widgets.checkout.stateDefault',
                    }}
                  />
                </Col>
              ) : null}
              <Col xs={12} md={4}>
                <CheckoutFormFieldText
                  required
                  name="unitPostal"
                  label={{
                    defaultMessage: 'Zip Code',
                    id: 'widgets.checkout.unitPostal',
                  }}
                />
              </Col>
            </Row>
          </>
        )
      }
      <Row>
        <Col xs={12} md={4}>
          <CheckoutSubmitButton
            submitLabel={{
              id: 'widgets.checkout.saveButton.submit',
              defaultMessage: 'Save and Continue',
            }}
            submitting={submitting}
            submittingLabel={{
              id: 'widgets.checkout.saveButton.submitting',
              defaultMessage: 'Saving...',
            }}
            {...(isReservation
              ? TRACK_CLICK_CHECKOUT_RESERVATION_STEP_ONE_SUBMIT
              : TRACK_CLICK_CHECKOUT_STEP_ONE_SUBMIT)
            }
          />
        </Col>
      </Row>
    </form>
  );
};

ContactInformationForm.propTypes = propTypes;
ContactInformationForm.defaultProps = defaultProps;

export default withForm({
  mapPropsToValues: (props) => {
    const formattedInitialValues = initialValues;

    if (props.formData) {
      formattedInitialValues.firstName = props.formData.tenant.firstName;
      formattedInitialValues.lastName = props.formData.tenant.lastName;
      formattedInitialValues.phone = {
        country: null,
        phoneNumber: props.formData.tenant.phone,
        unformattedPhoneNumber: props.formData.tenant.phone,
      };
      formattedInitialValues.email = props.formData.tenant.email;
      formattedInitialValues.address1 = props.formData.tenant.address1;
      formattedInitialValues.address2 = props.formData.tenant.address2;
      formattedInitialValues.city = props.formData.tenant.city;
      formattedInitialValues.state = props.formData.tenant.state;
      formattedInitialValues.postal = props.formData.tenant.postal;
      formattedInitialValues.smsOptIn = props.formData.tenant.smsOptIn;

      formattedInitialValues.activeDutyMilitary = props.formData.tenant.isMilitary ? 'yes' : 'no';
      if (formattedInitialValues.activeDutyMilitary === 'yes') {
        formattedInitialValues.commanderFirstName = props.formData.tenant.commanderFirstName;
        formattedInitialValues.commanderLastName = props.formData.tenant.commanderLastName;
        formattedInitialValues.unitAddress = props.formData.tenant.unitAddress;
        formattedInitialValues.unitCity = props.formData.tenant.unitCity;
        formattedInitialValues.unitState = props.formData.tenant.unitState;
        formattedInitialValues.unitPostal = props.formData.tenant.unitPostal;
      }
    }
    formattedInitialValues.isReservation = props.isReservation;
    formattedInitialValues.isFacilityGB = get(props, 'facility.address.country') === GB;
    formattedInitialValues.country = get(props, 'facility.address.country');

    return formattedInitialValues;
  },
  validationSchema,
  handleSubmit: (formPayload, formikBag) => (
    formikBag.props.onSubmit(
      formPayload,
      formikBag.props.currentStep,
      formikBag.props.nextStep,
      formikBag.props.scrollToRef,
    )
  ),
})(ContactInformationForm);
